// src/lib/analyticsFormat.ts
//
// Shared number/money formatting for the Dirección analytics pages (Resumen,
// Canales, Tiempos, Cobros, Hoteles). `null`/`undefined` always render as the
// dash placeholder — never coerced to 0 (Decision 11). Numbers follow the
// language the interface is shown in (es-ES or en-GB).
export const DASH = '—';

/** The number locale of the interface language: the saved UI locale, Spanish by default. */
export const uiNumberLocale = (): 'es-ES' | 'en-GB' => {
  try {
    return globalThis.localStorage?.getItem('locale') === 'en' ? 'en-GB' : 'es-ES';
  } catch {
    return 'es-ES';
  }
};

/** «9,2 %» in Spanish (non-breaking space before the sign), «9.2%» in English. */
const percentSign = (): string => (uiNumberLocale() === 'en-GB' ? '%' : ' %');

export const formatCount = (value: number | null | undefined): string =>
  value === null || value === undefined ? DASH : value.toLocaleString(uiNumberLocale());

export const formatPct = (value: number | null | undefined, digits = 1): string =>
  value === null || value === undefined
    ? DASH
    : `${new Intl.NumberFormat(uiNumberLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value)}${percentSign()}`;

export const formatMoney = (value: number | null | undefined, currency = 'EUR'): string =>
  value === null || value === undefined
    ? DASH
    : new Intl.NumberFormat(uiNumberLocale(), { style: 'currency', currency, maximumFractionDigits: 2 }).format(value);

/**
 * A money figure that may come in several currencies (P03 / D60): the single
 * amount when the API gave one, otherwise one amount per currency joined by
 * « · » — never added up. `null` list = not measurable («—»); an empty list =
 * no money at all (0 in the fallback currency).
 */
export const formatMoneyFigure = (
  amount: number | null | undefined,
  currency: string | null | undefined,
  byCurrency: ReadonlyArray<{ currency: string; amount: number }> | null | undefined
): string => {
  if (amount !== null && amount !== undefined) return formatMoney(amount, currency || 'EUR');
  if (!byCurrency) return DASH;
  if (byCurrency.length === 0) return formatMoney(0, currency || 'EUR');
  return byCurrency.map((c) => formatMoney(c.amount, c.currency)).join(' · ');
};

/** Amounts the API states in minor units (cents): `4515826` → «45.158,26 €». */
export const formatMinorMoney = (value: number | null | undefined, currency = 'EUR'): string =>
  value === null || value === undefined ? DASH : formatMoney(value / 100, currency);

/** A duration in seconds, in the two largest units that matter:
 *  «45 s», «4 min 59 s», «1 h 12 min», «2 d 3 h». */
export const formatSeconds = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  const sign = value < 0 ? '-' : '';
  const total = Math.round(Math.abs(value));
  if (total < 60) return `${sign}${total} s`;
  const pair = (major: number, majorUnit: string, minor: number, minorUnit: string) =>
    minor > 0 ? `${sign}${major} ${majorUnit} ${minor} ${minorUnit}` : `${sign}${major} ${majorUnit}`;
  if (total < 3600) return pair(Math.floor(total / 60), 'min', total % 60, 's');
  if (total < 86400) return pair(Math.floor(total / 3600), 'h', Math.floor((total % 3600) / 60), 'min');
  return pair(Math.floor(total / 86400), 'd', Math.floor((total % 86400) / 3600), 'h');
};

export type DeltaTrend = 'up' | 'down' | 'flat';

export interface FormattedDelta {
  text: string;
  trend: DeltaTrend;
}

/**
 * A change as a chip reads it: rounded first, and the direction taken from the
 * rounded figure, so a change that rounds to zero is «0 %» and flat — never a
 * red «-0 %» that reads as a drop.
 */
export const formatSignedDelta = (value: number, fractionDigits: number, suffix = ''): FormattedDelta => {
  const factor = 10 ** fractionDigits;
  const rounded = Math.round(Math.abs(value) * factor) / factor;
  const trend: DeltaTrend = rounded === 0 ? 'flat' : value > 0 ? 'up' : 'down';
  const sign = trend === 'up' ? '+' : trend === 'down' ? '-' : '';
  const number = new Intl.NumberFormat(uiNumberLocale(), { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }).format(rounded);
  const localSuffix = suffix === ' %' && uiNumberLocale() === 'en-GB' ? '%' : suffix;
  return { text: `${sign}${number}${localSuffix}`, trend };
};

/** Relative change of a count («+17 %»). Not for rates: use {@link formatPointsDelta}. */
export const formatDelta = (current: number, previous: number | null | undefined): FormattedDelta | undefined => {
  if (!previous) return undefined;
  return formatSignedDelta(((current - previous) / previous) * 100, 0, ' %');
};

/** Change of a rate in percentage points: 30 % → 40 % is «+10 pp», never «+33 %». */
export const formatPointsDelta = (
  current: number | null | undefined,
  previous: number | null | undefined
): FormattedDelta | undefined => {
  if (current === null || current === undefined || previous === null || previous === undefined) return undefined;
  return formatSignedDelta(current - previous, 1, ' pp');
};

/** True when a figure was withheld because its sample is below the minimum (the API returns null). */
export const isInsufficientSample = (sample: number, minimum: number | undefined): boolean =>
  minimum !== undefined && sample > 0 && sample < minimum;
